import {
  LockClosedIcon,
  MixerHorizontalIcon,
  TrackNextIcon,
} from '@radix-ui/react-icons';
import {
  Badge,
  Box,
  Card,
  Flex,
  IconButton,
  Progress,
  Text,
} from '@radix-ui/themes';
import { CustomIcon } from 'components/common/custom-icon';
import {
  LowerMachineIcon,
  R2FIcon,
  SuperAdminIcon,
} from 'components/common/custom-icon/shorthands';
import { DialogIconButton } from 'components/common/dialogs/button';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonMenu } from 'components/common/menu';
import { MachineAutoFireButton } from 'components/machine/buttons/auto-fire';
import { MachineCalibrateButton } from 'components/machine/buttons/calibrate';
import { DataCollectorContext } from 'components/machine/dialogs/installation/steps/data-collector/context';
import { AimingContext } from 'contexts/aiming.context';
import { MachineButton } from 'contexts/firing/button';
import { FiringContext } from 'contexts/firing/context';
import { MachineContext, MachineDialogMode } from 'contexts/machine.context';
import { TrainingContext } from 'contexts/training.context';
import { isFuture, parseISO } from 'date-fns';
import { CustomIconPath } from 'enums/custom.enums';
import { IgnoreAutoFireMode } from 'enums/firing';
import useLatest from 'hooks/useLatestRef';
import usePrevious from 'hooks/usePrevious';
import { t } from 'i18next';
import { ISendTraining } from 'interfaces/i-pitch-aiming';
import { TrainingHelper } from 'lib_ts/classes/training.helper';
import { DataCollectionStep } from 'lib_ts/enums/data-collector.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { SpecialMsPosition } from 'lib_ts/interfaces/machine-msg/i-special-mstarget';
import { ITrainingPitch } from 'lib_ts/interfaces/pitches';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { clamp, isEqual } from 'lodash';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';
import useDropBallStore from 'stores/use-drop-ball-store';
import useMachineStatusStore from 'stores/use-machine-status-store';

const BTN_CLASS = 'min-width-100px';

interface IProps {
  step: DataCollectionStep;

  pitch: ITrainingPitch;

  // 1-indexed
  pitchIndex: number;
  // length of pitches
  totalPitches: number;

  // e.g. to disable fire and ignore auto-fire when all shots for all pitches have been collected
  isComplete: boolean;

  // per pitch
  shotCount: number;
  // per pitch
  totalShots: number;

  // e.g. "Aiming X / Y" or "Analysis X / Y"
  shotSummary: ReactNode;

  // 0-1
  overallProgress: number;
  overallShotsRemaining: number;

  aimingShots: IMachineShot[];
}

export const DataCollectorStatusBar = (props: IProps) => {
  const s2mReady = useMachineStatusStore((s) => s.s2mReady);
  const noBall = useMachineStatusStore((s) => s.noBall);

  const { safeSetIgnoreAf } = useContext(FiringContext);
  const latestSafeSetIgnoreAf = useLatest(safeSetIgnoreAf);

  const { collection, handleToggleLC } = useContext(DataCollectorContext);

  const {
    mode: trainingMode,
    shots: trainingShots,
    startDate,
    setStartDate,
    resetMsgs: resetTrainingMessages,
  } = useContext(TrainingContext);

  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const calibrated = useMachineStatusStore((s) => s.calibrated);
  const canCalibrate = useMachineStatusStore((s) => s.canCalibrate);
  const fireStateDates = useMachineStatusStore((s) => s.fireStateDates);
  const r2fReady = useMachineStatusStore((s) => s.r2fReady);

  const droppingBall = useDropBallStore((s) => s.droppingBall);

  const {
    autoFire,
    lastBallCount,
    lastMSHash,
    isActive,
    setAutoFire,
    openDialog,
    specialMsTarget,
  } = useContext(MachineContext);

  const latestLastMSHash = useLatest(lastMSHash);

  const { setAndSendForTraining } = useContext(AimingContext);

  const { waiting, setWaiting, updateCollection } =
    useContext(DataCollectorContext);

  const sendData = useMemo(() => {
    // in case aimingShots updates after pitch changes
    const safeShots = props.aimingShots.filter(
      (m) => m.pitch_id === props.pitch._id
    );

    const output: ISendTraining = {
      collectionID: collection.collectionID,
      aiming: props.step === DataCollectionStep.AimingShots,
      trigger: `DCStatusBar > loadPitch`,
      aimingShots: safeShots,
    };

    return output;
  }, [collection.collectionID, props.step, props.aimingShots, props.pitch._id]);

  const previousSendData = usePrevious(sendData);

  // re-send to machine whenever payload changes
  useEffect(() => {
    if (!props.pitch) {
      return;
    }

    if (noBall) {
      // no need to resend if no ball
      return;
    }

    const timeout = setTimeout(async () => {
      const prevShotIDs = previousSendData?.aimingShots
        .map((s) => s._id)
        .sort();
      const currShotIDs = sendData?.aimingShots.map((s) => s._id).sort();

      if (currShotIDs.length > 0 && isEqual(prevShotIDs, currShotIDs)) {
        // try to stop ignoring AF
        latestSafeSetIgnoreAf.current(IgnoreAutoFireMode.Disabled);
      }

      if (props.pitch._mx_ms.matching_hash === latestLastMSHash.current) {
        // no need to resend if the last MS hash is the same
        return;
      }

      // only send when the shots actually changed
      await setAndSendForTraining({
        pitch: props.pitch,
        sendConfig: s2mReady ? sendData : undefined,
      }).then(() => {
        // allow the above to propagate before trying to stop ignoring AF
        setTimeout(() => {
          latestSafeSetIgnoreAf.current(IgnoreAutoFireMode.Disabled);
        }, 500);
      });
    }, 100);

    return () => clearTimeout(timeout);
  }, [noBall, s2mReady, props.pitch, sendData]);

  // ! a drop ball drops the machine out of R2F without changing sendData, so the effect above
  // ! never re-runs. BTC gets away with it because it gains an aiming shot every shot, which
  // ! keeps sendData changing; RT freezes its aiming shots once it reaches analysis shots, so
  // ! this is its only path back and the step would otherwise need the dialog reopened
  const [resendAfterDropBall, setResendAfterDropBall] = useState(false);

  useEffect(() => {
    if (!droppingBall) {
      return;
    }

    setResendAfterDropBall(true);
  }, [droppingBall]);

  useEffect(() => {
    if (!resendAfterDropBall || droppingBall) {
      return;
    }

    // wait for the ball to land, the machine cannot become ready without it
    if (lastBallCount !== 1) {
      return;
    }

    setResendAfterDropBall(false);

    // ! not gated on s2mReady: the machine reports a status that fails it while recovering from
    // ! the drop, and runSendTargetChecks does not require it, so waiting here deadlocks RT
    setAndSendForTraining({ pitch: props.pitch, sendConfig: sendData });
  }, [resendAfterDropBall, droppingBall, lastBallCount]);

  const [safeProgress, setSafeProgress] = useState(0);

  useEffect(() => {
    // value can only increase but not beyond 1
    setSafeProgress((prev) => clamp(props.overallProgress, prev, 1));
  }, [props.overallProgress]);

  const etaMinutes = useMemo(
    () => TrainingHelper.getETAForShots(props.overallShotsRemaining, 'min'),
    [props.overallShotsRemaining]
  );

  const r2fMessage = useMemo(() => {
    if (isFuture(fireStateDates.awaitFiringUntil)) {
      return t('common.firing');
    }

    if (lastBallCount !== 1) {
      return t('common.no-balls-msg');
    }

    if (waiting) {
      return t('common.waiting-for-data');
    }

    if (r2fReady) {
      return t('common.ready-to-fire');
    }

    return t('common.loading');
  }, [fireStateDates.awaitFiringUntil, lastBallCount, r2fReady, waiting]);

  const needsCalibration = isActive && !calibrated && canCalibrate;

  return (
    <ErrorBoundary identifier="DataCollectorStatusBar">
      <Card>
        <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
          <Flex
            gap={RADIX.FLEX.GAP.SM}
            align="center"
            style={{ height: '100%' }}
          >
            <Flex flexGrow="1" direction="column" gap={RADIX.FLEX.GAP.SM}>
              {/* row 1 */}
              <Flex gap={RADIX.FLEX.GAP.SM} justify="between">
                {/* left */}
                <Box>
                  <Text>{t('tr.est-x-min-remaining', { x: etaMinutes })}</Text>
                </Box>

                {/* right */}
                {props.shotSummary}
              </Flex>

              {/* row 2 */}
              <Progress value={safeProgress * 100} />
            </Flex>

            <Flex gap={RADIX.FLEX.GAP.SM}>
              <CommonMenu
                title="common.actions"
                trigger={
                  <IconButton
                    size={RADIX.BUTTON.SIZE.TABLE}
                    variant={RADIX.BUTTON.VARIANT.TABLE}
                  >
                    <MixerHorizontalIcon />
                  </IconButton>
                }
                actions={[
                  {
                    group: '_1',
                    label: 'Skip Current Pitch',
                    prefixIcon: <TrackNextIcon />,
                    disabled:
                      !lastMSHash ||
                      autoFire ||
                      props.shotCount >= props.totalShots,
                    onClick: async () => {
                      if (!collection.pitches) {
                        return;
                      }

                      const pitch = collection.pitches.find(
                        (p) => p._id === props.pitch._id
                      );

                      if (!pitch) {
                        // shouldn't trigger
                        return;
                      }

                      // mark the pitch as skipped
                      pitch.skipped = true;

                      // save the change
                      updateCollection({
                        pitches: collection.pitches,
                      });
                    },
                  },
                  {
                    group: '_1',
                    label: 'Include Low Confidence Shots',

                    prefixIcon: (
                      <CustomIcon
                        icon={
                          collection.include_low_confidence
                            ? CustomIconPath.SwitchOn
                            : CustomIconPath.SwitchOff
                        }
                      />
                    ),

                    // e.g. a HWD (based on a RT that allowed LC) cannot be told reject LC
                    disabled: collection.lock_low_confidence,

                    suffixIcon: collection.lock_low_confidence ? (
                      <LockClosedIcon />
                    ) : undefined,

                    onClick: () => handleToggleLC(),
                  },
                  {
                    group: '_2',
                    label: 'common.ready-status',
                    prefixIcon: <R2FIcon />,
                    suffixIcon: <SuperAdminIcon />,
                    invisible: !userRoleFeatureFlags.r2fInspector,
                    onClick: () => openDialog(MachineDialogMode.Status),
                  },
                ]}
                skipSort
              />

              <DialogIconButton
                color={RADIX.COLOR.WARNING}
                icon={<LowerMachineIcon />}
                tooltip={t('common.lower-machine').toString()}
                onClick={() => {
                  setAutoFire(false);
                  specialMsTarget(SpecialMsPosition.lowered);
                }}
              />

              <MachineAutoFireButton as="dialog-button" className={BTN_CLASS} />

              {/* fire + resend */}
              {!needsCalibration && (
                <MachineButton
                  identifier="DCStatusBarMachineButton"
                  className={BTN_CLASS}
                  as="dialog-button"
                  fireData={{
                    mode: 'manual',
                    trigger: 'click',
                    training: true,
                    training_mode: trainingMode,
                    hitter_id: undefined,
                    tags: 'DC',
                  }}
                  sendData={sendData}
                  beforeFire={(isReady) => {
                    if (!isReady) {
                      return;
                    }

                    setWaiting(true);
                    resetTrainingMessages();
                  }}
                  afterFire={(result) => {
                    if (startDate) {
                      // a date was already set
                      return;
                    }

                    if (!result.success) {
                      // fire failed
                      return;
                    }

                    if (!result.event) {
                      // fire event wasn't created
                      return;
                    }

                    // mark the start date for shot queries
                    setStartDate(parseISO(result.event._created));
                  }}
                  trainingShots={trainingShots}
                />
              )}

              {needsCalibration && <MachineCalibrateButton />}
            </Flex>
          </Flex>

          <Flex gap={RADIX.FLEX.GAP.SM} justify="between">
            <Box>
              <Text color={RADIX.COLOR.SECONDARY}>{r2fMessage}</Text>
            </Box>

            {collection.include_low_confidence && (
              <Badge color={RADIX.COLOR.WARNING}>
                Allowing Low Confidence Shots
              </Badge>
            )}
          </Flex>
        </Flex>
      </Card>
    </ErrorBoundary>
  );
};

// ts will complain without a displayName
DataCollectorStatusBar.displayName = 'DataCollectorStatusBar';
